import React from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import { Star as StarIcon } from '@mui/icons-material';
import { font } from '../../utils/fontHelper';
import ImageShimmer from './ImageShimmer';
import { getAppConfig } from '@watchlist/shared';

const TMDB_IMG = (path: string | undefined | null, size = 'w200'): string | undefined => {
  if (!path || path === 'N/A') return undefined;
  if (path.startsWith('http')) return path;
  const normalized = path.startsWith('/') ? path : `/${path}`;
  return `https://image.tmdb.org/t/p/${size}${normalized}`;
};

interface CompactMovieCardProps {
  poster?: string | null;
  title: string;
  year?: string;
  genre?: string;
  rating?: number;
  onClick?: () => void;
  dense?: boolean;
  noBorder?: boolean;
}

const CompactMovieCard: React.FC<CompactMovieCardProps> = ({
  poster,
  title,
  year,
  genre,
  rating,
  onClick,
  dense = false,
  noBorder = false,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const posterSrc = TMDB_IMG(poster, 'w200');
  const glassCard = getAppConfig().featureGlassmorphicMovieCard;

  const posterDim = dense ? { w: 52, h: 60 } : { w: 70, h: 76 };
  const cardPadding = dense ? 1.25 : { xs: 1.5, sm: 2 };
  const cardGap = dense ? 1.25 : { xs: 1.5, sm: 2 };
  const minHeight = dense ? 76 : { xs: 100, sm: 110 };

  const posterBox = (
    <Box sx={{
      width: posterDim.w,
      height: posterDim.h,
      borderRadius: 1.5,
      overflow: 'hidden',
      flexShrink: 0,
      bgcolor: isDark ? '#3A3A3C' : '#E0E0E0',
    }}>
      <ImageShimmer
        src={posterSrc}
        alt={title}
        width="100%"
        height="100%"
        objectFit="cover"
        borderRadius={1}
      />
    </Box>
  );

  const infoContent = (
    <Box sx={{ flex: 1, minWidth: 0, py: 0.5, display: 'flex', flexDirection: 'column' }}>
      <Typography sx={{
        fontFamily: font(),
        fontWeight: dense ? 500 : 500,
        color: theme.palette.text.primary,
        fontSize: dense ? '14px' : { xs: '15px', sm: '16px' },
        mb: 0.25,
        lineHeight: 1.4,
        overflow: 'hidden',
        display: '-webkit-box',
        WebkitLineClamp: 2,
        WebkitBoxOrient: 'vertical',
        wordBreak: 'break-word',
      }}>
        {title}
      </Typography>

      {(year || genre) && (
        <Typography sx={{
          fontFamily: font(),
          color: glassCard ? 'rgba(255,255,255,0.55)' : (isDark ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.6)'),
          fontSize: dense ? '11px' : '12px',
          mb: 0.5,
          lineHeight: 1.33,
          overflow: 'hidden',
          display: '-webkit-box',
          WebkitLineClamp: 1,
          WebkitBoxOrient: 'vertical',
        }}>
          {[year, genre].filter(Boolean).join(' ‧ ')}
        </Typography>
      )}

      {rating != null && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <StarIcon sx={{ fontSize: dense ? 13 : 14, color: '#FFD700' }} />
          <Typography sx={{
            fontFamily: font(),
            fontWeight: 500,
            fontSize: dense ? 12 : { xs: 12, sm: 13 },
            color: glassCard ? 'rgba(255,255,255,0.9)' : (isDark ? 'rgba(255,255,255,0.9)' : 'rgba(0,0,0,0.9)'),
            lineHeight: 1.33,
          }}>
            {rating}/5
          </Typography>
        </Box>
      )}
    </Box>
  );

  if (glassCard) {
    return (
      <Box
        onClick={onClick}
        sx={{
          position: 'relative',
          borderRadius: 3,
          cursor: onClick ? 'pointer' : 'default',
          minHeight,
          overflow: 'hidden',
          border: noBorder ? 'none' : '1px solid rgba(255,255,255,0.12)',
          transition: 'opacity 0.2s ease-in-out',
          '&:hover': onClick ? { opacity: 0.9 } : {},
        }}
      >
        <Box sx={{
          position: 'absolute', inset: 0,
          backgroundImage: `url(${posterSrc})`,
          backgroundSize: 'cover', backgroundPosition: 'center',
          filter: 'blur(8px) brightness(0.2)',
          transform: 'scale(1.15)',
        }} />
        <Box sx={{
          position: 'relative', zIndex: 1,
          display: 'flex', alignItems: 'flex-start',
          gap: cardGap, p: cardPadding,
          background: 'rgba(29, 26, 26, 0.08)',
          '@media (max-width: 400px)': dense ? {} : { p: 1, gap: 1 },
        }}>
          {posterBox}
          {infoContent}
        </Box>
      </Box>
    );
  }

  return (
    <Box
      onClick={onClick}
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: cardGap,
        p: cardPadding,
        borderRadius: 3,
        cursor: onClick ? 'pointer' : 'default',
        bgcolor: isDark ? '#2C2C2E' : '#F0EFF0',
        minHeight,
        overflow: 'hidden',
        transition: 'background-color 0.2s ease-in-out',
        '&:hover': onClick ? { bgcolor: isDark ? '#3A3A3C' : '#E5E5EA' } : {},
        '@media (max-width: 400px)': dense ? {} : { p: 1, gap: 1 },
      }}
    >
      {posterBox}
      {infoContent}
    </Box>
  );
};

export default CompactMovieCard;
